/**
 * Dashboards — slim orchestrator.
 *
 * Owns all state + side-effects; delegates rendering via a `ctx` prop bag:
 *
 *   - DashboardDetailView         (inline opened dashboard)
 *   - UnifiedDashboardsTable      (Details view — My dashboards + Prebuilt in one table, default)
 *   - UnifiedDashboardsTiles      (Tiles view — "My dashboards" + "Prebuilt dashboards" sections)
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, PageContent } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Plus, Search, X, Lock, LayoutGrid, User, Layers } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useEntitlements } from "@/lib/use-entitlements";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useConfirm } from "@/lib/confirm-context";
import { useScopeFilter } from "@/lib/scope-filter-context";
import ViewToggle from "@/components/ViewToggle";
import AddSourceDialog from "@/components/dashboards/AddSourceDialog";
import SchemaMatchErrorDialog from "@/components/dashboards/SchemaMatchErrorDialog";
import DashboardDetailView from "@/components/dashboards/tabs/DashboardDetailView";
import UnifiedDashboardsTable from "@/components/dashboards/tabs/UnifiedDashboardsTable";
import UnifiedDashboardsTiles from "@/components/dashboards/tabs/UnifiedDashboardsTiles";
import { useAuth } from "@/lib/auth-context";
import { useSettings } from "@/lib/settings-context";
import { useBreadcrumbLabel } from "@/lib/breadcrumb-labels";
import { DEFAULT_PAGE_SIZE } from "@/lib/pagination";

const PAGE_SIZE = DEFAULT_PAGE_SIZE;

// Tiles-view type chips ("All dashboards" / "My dashboards" / "Prebuilt").
const TYPE_CHIPS = [
    { key: "all", label: "All dashboards", Icon: LayoutGrid },
    { key: "my", label: "My dashboards", Icon: User },
    { key: "prebuilt", label: "Prebuilt", Icon: Layers },
];


export default function Dashboards() {
    const { user } = useAuth();
    const { getAccessLevel } = useSettings();
    const canWriteDashboard = ["A", "W"].includes(getAccessLevel("dashboards"));
    const isSuperAdmin = user?.role === "super_admin";
    // Plan → RBAC → limit → UI. New Dashboard locks when custom dashboards
    // aren't entitled OR the org dashboard limit is reached. SA never locked.
    const { entitled: planEntitled, limitReached: planLimitReached, openUpgrade } = useEntitlements();
    const dashCustomOff = !isSuperAdmin && !planEntitled("custom_dashboards");
    const dashLimitHit = !isSuperAdmin && planLimitReached("max_dashboards");
    const dashCreateLocked = dashCustomOff || dashLimitHit;
    const dashLockReason = dashCustomOff
        ? "Custom dashboards aren't included in your current plan. Ask your admin to upgrade to create dashboards."
        : "You've reached the number of dashboards included in your plan. Delete one or upgrade to create more.";
    // Read-only roles must not see Connect-data / Add-source affordances.
    const canWriteData = ["super_admin", "org_admin", "entity_admin"].includes(user?.role);
    const [templates, setTemplates] = useState([]);
    const [dashboards, setDashboards] = useState([]);
    // Tiles-view type chip. `?tab=templates` (onboarding deep-link) still
    // lands users on the prebuilt templates.
    const [typeFilter, setTypeFilter] = useState(() => {
        try {
            return new URLSearchParams(window.location.search).get("tab") === "templates" ? "prebuilt" : "all";
        } catch { return "all"; }
    });
    // Details is the default view every time the Dashboards page opens;
    // Tiles is the secondary option for the current visit.
    const [viewMode, setViewMode] = useState("details");
    const [open, setOpen] = useState(false);
    const [opened, setOpened] = useState(null); // dashboard viewed inline
    const [name, setName] = useState("");
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState(""); // search filter — applies to both tabs
    const [category, setCategory] = useState("all"); // category filter — applies to both tabs
    const [galleryOpen, setGalleryOpen] = useState(false);
    const [sourcesOpen, setSourcesOpen] = useState(false);
    const [addSourceOpen, setAddSourceOpen] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [schemaMismatch, setSchemaMismatch] = useState(null);
    const [tileSourceTarget, setTileSourceTarget] = useState(null); // {id, name}
    const [pushdownOpen, setPushdownOpen] = useState(false);
    const nav = useNavigate();
    const location = useLocation();
    // Build a /data wiring URL that remembers where we came from so the
    // Cancel button on the wiring banner returns the user to exactly that page.
    const wiringHref = (params, fromOverride) => {
        const sp = new URLSearchParams({ action: "connect", ...params });
        sp.set("from", fromOverride || (location.pathname + location.search));
        return `/data?${sp.toString()}`;
    };
    const { id } = useParams();
    const confirm = useConfirm();
    // iter — Register the opened dashboard's friendly name for the
    // breadcrumb bar so /dashboards/{id} reads as "… › Dashboards ›
    // <Dashboard title>" instead of a UUID.
    useBreadcrumbLabel(id, opened?.name || opened?.title);
    // iter-119ba — SA top-bar scope filter (Tenant → Org) drives the
    // /api/dashboards listing for SA. Non-SA users always pass null/null
    // and the backend ignores the params (RBAC-enforced server-side).
    const { tenantId: scopeTenantId, orgId: scopeOrgId } = useScopeFilter();

    const load = useCallback(async () => {
        const scopeParams = {};
        if (scopeTenantId) scopeParams.tenant_id = scopeTenantId;
        if (scopeOrgId) scopeParams.org_id = scopeOrgId;
        const [t, d] = await Promise.all([
            api.get("/dashboards/templates"),
            api.get("/dashboards", { params: scopeParams }),
        ]);
        const merged = (t.data || []).map((s) => {
            const instance = (d.data || []).find(
                (dash) => dash.source_gallery_template_id === s.id || dash.template_slug === s.id
            );
            return {
                ...s,
                instance: instance ? { id: instance.id } : null,
                _model_bound: Array.isArray(s.bind_to_models) && s.bind_to_models.length > 0,
            };
        });
        setTemplates(merged);
        setDashboards(d.data);
        if (id) {
            const all = [...merged, ...d.data];
            const found = all.find((x) => x.id === id);
            // iter-113: hydrate from detail endpoint so data_scope_warning lands
            if (found) {
                if (found.is_template) {
                    // Templates always open in the dedicated preview (KPI strip + sample data).
                    nav(`/dashboards/preview/${found.id}`, { replace: true });
                    return;
                } else {
                    try {
                        const r = await api.get(`/dashboards/${found.id}`);
                        setOpened(r.data);
                    } catch {
                        setOpened(found);
                    }
                }
            } else {
                // iter-120 round-4 · The dashboard isn't in the
                // scope-filtered list (e.g. SA pinned a scope to a
                // different tenant, OR the user landed on a deep-link
                // outside their current filter). Always honour the URL
                // by fetching the dashboard directly — RBAC at the
                // endpoint enforces access. If the fetch fails (403/
                // 404), we surface that via the existing catch path.
                try {
                    const r = await api.get(`/dashboards/${id}`);
                    setOpened(r.data);
                } catch {
                    setOpened(null);
                }
            }
        }
        setLoading(false);
    }, [scopeTenantId, scopeOrgId, id]);
    // iter-120 · clear the inline detail view when the user navigates
    // back to `/dashboards` (no `:id` in URL) — e.g. by clicking the
    // sidebar "Dashboards" link while already on `/dashboards/<id>`.
    // Without this, `opened` stayed set so the page continued to show
    // the detail view even though the URL had returned to the list.
    useEffect(() => {
        if (!id) setOpened(null);
    }, [id]);
    useEffect(() => { load(); }, [load]);

    // iter-119u (C2): refetch when SA toggles template enablement so OA
    // sees newly-enabled prebuilts without a hard refresh.
    // iter-119ay: ALSO refetch on tab visibility change so cross-browser /
    // cross-device scenarios (SA on laptop, OA on phone) pick up the new
    // enablement when the user comes back to the tab. The intra-tab
    // `gallery:templates-changed` event only fires within the same
    // browser session.
    useEffect(() => {
        const onChanged = () => load();
        const onVisible = () => { if (document.visibilityState === "visible") load(); };
        window.addEventListener("gallery:templates-changed", onChanged);
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            window.removeEventListener("gallery:templates-changed", onChanged);
            document.removeEventListener("visibilitychange", onVisible);
        };
  }, [load]);

    // iter-113: list payload omits per-dashboard fields. Always re-fetch
    // detail before opening so the inline view has everything.
    // iter-120 · navigate to `/dashboards/<id>` so the URL reflects the
    // open detail view. Required for the sidebar "Dashboards" link to
    // pop back to the list — without a URL transition the click target
    // would resolve to the same path the user is already on (`useParams
    // .id` would stay undefined) and the `opened` state would be sticky.
    const openDashboard = async (d) => {
        if (!d?.id) return;
        try {
            const r = await api.get(`/dashboards/${d.id}`);
            setOpened(r.data);
        } catch (e) {
            setOpened(d);
            toast.error(e?.response?.data?.detail || "Could not load dashboard details");
        }
        nav(`/dashboards/${d.id}`);
    };

    const clone = async (tpl) => {
        // Every template is a Sense AI Builder prebuilt. Clone via the
        // idempotent instantiator — a second call returns the same dashboard.
        const params = {};
        if (scopeTenantId) params.tenant_id = scopeTenantId;
        if (scopeOrgId) params.org_id = scopeOrgId;
        const r = await api.post(`/dashboards/from-template/${tpl.id}`, null, { params });
        setDashboards((prev) => {
            const filtered = prev.filter((d) => d.id !== r.data.id);
            return [r.data, ...filtered];
        });
        toast.success(tpl.instance ? `Opened "${tpl.name}"` : `Cloned "${tpl.name}"`);
        setOpened(r.data);
        nav(`/dashboards/${r.data.id}`);
        return r.data;
    };

    // Delete a template-instantiated dashboard, leaving the template intact.
    const delTemplateInstance = async (tpl) => {
        const instId = tpl.instance?.id;
        if (!instId) return;
        const ok = await confirm({
            title: `Delete instantiated "${tpl.name}"?`,
            description: "Removes the dashboard instance from My Dashboards. The template stays available — you can re-instantiate it at any time.",
            confirmLabel: "Delete instance",
            destructive: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/dashboards/${instId}`);
            toast.success("Instance deleted");
            setDashboards((prev) => prev.filter((d) => d.id !== instId));
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const createBlank = async () => {
        if (!name.trim()) return;
        const r = await api.post("/dashboards", { name, widgets: [] });
        toast.success("Dashboard created");
        setDashboards([r.data, ...dashboards]);
        setOpen(false); setName("");
        setOpened(r.data);
        nav(`/dashboards/${r.data.id}`);
    };

    const del = async (id, name) => {
        const ok = await confirm({
            title: `Delete dashboard "${name}"?`,
            description: "Removes the dashboard. If it was created from a template, the template stays available — you can re-instantiate any time.",
            confirmLabel: "Delete dashboard", destructive: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/dashboards/${id}`);
            toast.success("Dashboard deleted");
            setDashboards((prev) => prev.filter((d) => d.id !== id));
            // Refresh templates so template-instance buttons (Open ↔ Instantiate) update.
            try {
                const tpl = await api.get("/templates");
                setTemplates((prev) => {
                    const legacy = prev.filter((p) => !p._model_bound);
                    return [
                        ...(tpl.data || []).map((m) => ({ ...m, _model_bound: true, widgets: m.widgets || [] })),
                        ...legacy,
                    ];
                });
            } catch { /* non-blocking */ }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    // iter-67: Sense AI Builder prebuilts ARE Gallery items
    // (kind=prebuilt_dashboard). Route through DELETE /gallery/{id}
    // so super_admin/owner enforcement lives in gallery_routes.
    const savePlaybookFromTemplate = async (tpl) => {
        if (!isSuperAdmin) return;
        try {
            const sourceId = tpl?.instance?.id;
            if (sourceId) {
                await api.post(`/sense-ai-builder/playbooks/from-dashboard/${sourceId}`, {
                    name: tpl.name,
                });
            } else {
                await api.post(`/sense-ai-builder/playbooks/from-template/${tpl.id}`, {});
            }
            toast.success(`Saved "${tpl.name}" as playbook`, {
                description: "Future Builder prompts matching its keywords will use it as RAG context. Edit keywords in /playbooks.",
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save as playbook");
        }
    };

    const hideTemplate = async (tpl) => {
        if (!isSuperAdmin) return;
        const ok = await confirm({
            title: "Delete template?",
            description: (
                <>Permanently delete the prebuilt template <span className="font-medium">{tpl.name}</span>?
                    Existing instantiated dashboards keep working — only the template definition is removed.
                </>
            ),
            confirmLabel: "Delete template",
            destructive: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/gallery/${tpl.id}`);
            toast.success(`${tpl.name} removed from prebuilts`);
            setTemplates((prev) => prev.filter((t) => t.id !== tpl.id));
            // Broadcast so Platform Templates / Visual Gallery refetch.
            window.dispatchEvent(new CustomEvent("gallery:templates-changed", {
                detail: { id: tpl.id, action: "deleted" },
            }));
            // Backend cascades dashboard instances bound to this template;
            // reload so My Dashboards reflects the cascade.
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    // Derived: filtered lists honoring search + category across BOTH tabs
    const matches = useCallback((d) => {
        const qMatch = !q || (d.name || "").toLowerCase().includes(q.toLowerCase()) ||
            (d.description || "").toLowerCase().includes(q.toLowerCase());
        const cMatch = category === "all" || (d.category || "general") === category;
        return qMatch && cMatch;
    }, [q, category]);
    // iter-121.131 — Pagination + descending order.
    //
    // (1) Sort by `created_at` DESC so newly-added dashboards land at
    //     the TOP of "My dashboards", matching the user's "added
    //     dashboard should appear first" expectation. Falls back to
    //     `updated_at` then `id` for back-compat with older rows that
    //     might lack a `created_at`. We sort the FULL list (not just
    //     the filtered slice) so the order is stable across searches.
    //
    // (2) Pagination — 12 items per page (3 cols × 4 rows in the tile
    //     grid; matches the page's natural density). State is held in
    //     the URL via `?my_page=N&prebuilt_page=N` so deep-links and
    //     reloads survive. Page resets to 1 whenever the search query
    //     or category filter changes (otherwise paging beyond the end
    //     of the filtered list would render empty).
    const _sortDesc = (a, b) => {
        const ta = +new Date(a.created_at || a.updated_at || 0);
        const tb = +new Date(b.created_at || b.updated_at || 0);
        if (ta !== tb) return tb - ta;
        return (b.id || "").localeCompare(a.id || "");
    };
    const sortedDashboards = useMemo(() => [...dashboards].sort(_sortDesc), [dashboards]);
    const sortedTemplates = useMemo(() => [...templates].sort(_sortDesc), [templates]);
    const filteredDashboards = useMemo(() => sortedDashboards.filter(matches), [sortedDashboards, matches]);
    const filteredTemplates  = useMemo(() => sortedTemplates.filter(matches),  [sortedTemplates, matches]);

    // Details view: ONE combined list (My dashboards first, then Prebuilt —
    // each keeps its created_at DESC order) paged via `?page=N`.
    // Tiles view: each section pages independently via `?my_page=N` /
    // `?prebuilt_page=N`. All page params reset when search/category change.
    const combinedRows = useMemo(() => [
        ...filteredDashboards.map((item) => ({ kind: "my", item })),
        ...filteredTemplates.map((item) => ({ kind: "prebuilt", item })),
    ], [filteredDashboards, filteredTemplates]);

    const [searchParams, setSearchParams] = useSearchParams();
    const readPage = (key) => Math.max(1, parseInt(searchParams.get(key) || "1", 10) || 1);
    const page = readPage("page");
    const myPage = readPage("my_page");
    const prebuiltPage = readPage("prebuilt_page");
    const pageCount = Math.max(1, Math.ceil(combinedRows.length / PAGE_SIZE));
    const myPageCount = Math.max(1, Math.ceil(filteredDashboards.length / PAGE_SIZE));
    const prebuiltPageCount = Math.max(1, Math.ceil(filteredTemplates.length / PAGE_SIZE));
    const PAGE_KEYS = ["page", "my_page", "prebuilt_page"];
    // Defensive clamp — if filters shrink a list past the current page,
    // slide back to the new last page.
    useEffect(() => {
        if (loading) return;
        const next = new URLSearchParams(searchParams);
        let changed = false;
        const max = { page: pageCount, my_page: myPageCount, prebuilt_page: prebuiltPageCount };
        for (const k of PAGE_KEYS) {
            const cur = readPage(k);
            if (cur > max[k]) { next.set(k, String(max[k])); changed = true; }
            if (next.get(k) === "1") { next.delete(k); changed = changed || searchParams.has(k); }
        }
        if (changed) setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageCount, myPageCount, prebuiltPageCount, loading]);
    // Typing in search / picking a category returns every list to page 1.
    const filterKey = `${q}\u0000${category}`;
    const [lastFilterKey, setLastFilterKey] = useState(filterKey);
    useEffect(() => {
        if (filterKey === lastFilterKey) return;
        setLastFilterKey(filterKey);
        const next = new URLSearchParams(searchParams);
        let changed = false;
        for (const k of PAGE_KEYS) if (next.has(k)) { next.delete(k); changed = true; }
        if (changed) setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterKey]);
    const pagedRows = useMemo(
        () => combinedRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [combinedRows, page],
    );
    const pagedDashboards = useMemo(
        () => filteredDashboards.slice((myPage - 1) * PAGE_SIZE, myPage * PAGE_SIZE),
        [filteredDashboards, myPage],
    );
    const pagedTemplates = useMemo(
        () => filteredTemplates.slice((prebuiltPage - 1) * PAGE_SIZE, prebuiltPage * PAGE_SIZE),
        [filteredTemplates, prebuiltPage],
    );
    const setPageParam = (key) => (n) => {
        const next = new URLSearchParams(searchParams);
        if (n <= 1) next.delete(key);
        else next.set(key, String(n));
        setSearchParams(next, { replace: true });
    };
    const setPage = setPageParam("page");
    const setMyPage = setPageParam("my_page");
    const setPrebuiltPage = setPageParam("prebuilt_page");
    const categories = ["all", ...Array.from(new Set(
        [...dashboards, ...templates].map((x) => x.category || "general")
    )).sort()];

    if (opened) {
        return (
            <DashboardDetailView ctx={{
                opened, setOpened, setDashboards, nav,
                canWriteData, canWriteDashboard, isSuperAdmin,
                wiringHref, clone, load,
                sourcesOpen, setSourcesOpen,
                addSourceOpen, setAddSourceOpen,
                shareOpen, setShareOpen,
                galleryOpen, setGalleryOpen,
                pushdownOpen, setPushdownOpen,
                schemaMismatch, setSchemaMismatch,
            }} />
        );
    }

    const prebuiltLocked = !isSuperAdmin && !planEntitled("prebuilt_dashboards");
    const customLocked = !isSuperAdmin && !planEntitled("custom_dashboards");

    // "Connect data" on a prebuilt template: attach to the existing instance,
    // or instantiate first (same idempotent endpoint as Use) then attach.
    const onConnectData = async (t) => {
        let target = t.instance;
        if (!target?.id) {
            try {
                const params = {};
                if (scopeTenantId) params.tenant_id = scopeTenantId;
                if (scopeOrgId) params.org_id = scopeOrgId;
                const r = await api.post(`/dashboards/from-template/${t.id}`, null, { params });
                target = r.data;
                load();
            } catch (err) {
                toast.error(err?.response?.data?.detail || "Could not start template");
                return;
            }
        }
        setTileSourceTarget({ id: target.id, name: target.name || t.name });
    };

    const listCtx = {
        // My dashboards
        canWriteData, canWriteDashboard, openDashboard, setTileSourceTarget, del,
        customLocked,
        // Prebuilt templates
        isSuperAdmin, nav, clone, delTemplateInstance, hideTemplate, savePlaybookFromTemplate,
        onConnectData, prebuiltLocked, openUpgrade,
    };
    const canCreate = canWriteDashboard && !dashCreateLocked;
    const typeCounts = {
        all: filteredDashboards.length + filteredTemplates.length,
        my: filteredDashboards.length,
        prebuilt: filteredTemplates.length,
    };

    return (
        <div className="fadein">
            <PageHeader
                eyebrow="Visualize"
                title="Dashboards"
                description="Explore prebuilt dashboards or create your own."
                testId="dashboards-header"
                actions={
                    canWriteDashboard && (
                        dashCreateLocked ? (
                            <Button variant="outline" className="rounded-md opacity-90"
                                title={dashLockReason}
                                onClick={() => openUpgrade()}
                                data-testid="dashboard-create-button-locked">
                                <Lock className="h-4 w-4 sm:mr-1.5" />
                                <span className="hidden sm:inline">New dashboard</span>
                                <Badge variant="secondary" className="ml-2 text-[9px] font-mono uppercase">Upgrade</Badge>
                            </Button>
                        ) : (
                            <Button onClick={() => setOpen(true)} className="rounded-md shadow-sm" data-testid="dashboard-create-button" title="New dashboard">
                                <Plus className="h-4 w-4 sm:mr-1.5" />
                                <span className="hidden sm:inline">New dashboard</span>
                            </Button>
                        )
                    )
                }
            />
            <PageContent>
                {/* Global filter bar — applies to BOTH My dashboards and Templates */}
                <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="dashboards-filter-bar">
                    <div className="relative flex-1 min-w-[260px] max-w-md">
                        <Search className="h-4 w-4 text-muted-foreground absolute left-3.5 top-1/2 -translate-y-1/2" />
                        <input value={q} onChange={(e) => setQ(e.target.value)}
                            placeholder="Search dashboards and templates..."
                            className="w-full h-10 pl-10 pr-3 bg-card border border-border rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all"
                            data-testid="dashboards-search" />
                    </div>
                    <Select value={category} onValueChange={setCategory}>
                        <SelectTrigger className="h-10 w-48 rounded-lg text-sm bg-card shadow-sm border-border" data-testid="dashboards-category-filter">
                            <SelectValue placeholder="All categories" />
                        </SelectTrigger>
                        <SelectContent>
                            {categories.map((c) => (
                                <SelectItem key={c} value={c} data-testid={`dashboards-category-opt-${c}`}>
                                    {c === "all" ? "All categories" : c}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {(q || category !== "all") && (
                        <Button variant="ghost" size="sm" className="h-10 rounded-lg gap-1"
                            onClick={() => { setQ(""); setCategory("all"); }}
                            data-testid="dashboards-filter-clear">
                            <X className="h-3.5 w-3.5" /> Clear
                        </Button>
                    )}
                    <div className="ml-auto">
                        <ViewToggle value={viewMode} onChange={setViewMode} testIdPrefix="dashboards-view" />
                    </div>
                </div>
                {viewMode === "tiles" && (
                    <div className="flex items-center gap-2 flex-wrap mb-5" role="tablist" aria-label="Dashboard type" data-testid="dashboards-type-chips">
                        {TYPE_CHIPS.map(({ key, label, Icon }) => {
                            const active = typeFilter === key;
                            return (
                                <button key={key} type="button" role="tab" aria-selected={active}
                                    onClick={() => setTypeFilter(key)}
                                    className={`inline-flex items-center gap-2 h-9 px-3.5 rounded-lg border text-sm font-medium transition-colors ${active
                                        ? "border-primary/30 bg-primary/10 text-foreground"
                                        : "border-border bg-card text-muted-foreground hover:text-foreground hover:bg-secondary/50"}`}
                                    data-testid={`dashboards-type-${key}`}>
                                    <Icon className={`h-4 w-4 ${active ? "text-primary" : ""}`} />
                                    {label}
                                    <span className="text-muted-foreground">·</span>
                                    <span className="font-mono text-xs" data-testid={`dashboards-type-${key}-count`}>{typeCounts[key]}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
                {viewMode === "details" ? (
                    <>
                        <UnifiedDashboardsTable rows={pagedRows} ctx={listCtx} loading={loading} />
                        {combinedRows.length > PAGE_SIZE && (
                            <PagerFooter page={page} pageCount={pageCount} pageSize={PAGE_SIZE}
                                total={combinedRows.length} onChange={setPage} testIdPrefix="dashboards" />
                        )}
                    </>
                ) : (
                    <UnifiedDashboardsTiles
                        ctx={listCtx}
                        loading={loading}
                        showMy={typeFilter !== "prebuilt"}
                        showPrebuilt={typeFilter !== "my"}
                        myRows={pagedDashboards}
                        myTotal={filteredDashboards.length}
                        prebuiltRows={pagedTemplates}
                        prebuiltTotal={filteredTemplates.length}
                        hasAnyDashboards={dashboards.length > 0}
                        onCreate={canCreate ? () => setOpen(true) : null}
                        myPager={filteredDashboards.length > PAGE_SIZE ? (
                            <PagerFooter page={myPage} pageCount={myPageCount} pageSize={PAGE_SIZE}
                                total={filteredDashboards.length} onChange={setMyPage} testIdPrefix="my-dashboards" />
                        ) : null}
                        prebuiltPager={filteredTemplates.length > PAGE_SIZE ? (
                            <PagerFooter page={prebuiltPage} pageCount={prebuiltPageCount} pageSize={PAGE_SIZE}
                                total={filteredTemplates.length} onChange={setPrebuiltPage} testIdPrefix="prebuilts" />
                        ) : null}
                    />
                )}
            </PageContent>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>New dashboard</DialogTitle>
                        <DialogDescription>Give your dashboard a name. You can add widgets after it's created.</DialogDescription>
                    </DialogHeader>
                    <div>
                        <label className="eyebrow mb-1.5 block">Name</label>
                        <input value={name} onChange={(e) => setName(e.target.value)} className="w-full h-10 px-3 bg-card border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all" data-testid="dashboard-name-input" />
                    </div>
                    <DialogFooter>
                        <Button onClick={createBlank} className="rounded-md" data-testid="dashboard-create-submit">Create</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Shared AddSourceDialog driven by tile/template clicks across
                My Dashboards + Prebuilts — single source of truth for
                "Connect data" / "Add source" UX. */}
            <AddSourceDialog
                open={!!tileSourceTarget}
                onClose={() => setTileSourceTarget(null)}
                dashboardId={tileSourceTarget?.id}
                dashboardName={tileSourceTarget?.name}
                onAttached={() => { setTileSourceTarget(null); load(); }}
                onConnectNew={() => { const t = tileSourceTarget; setTileSourceTarget(null); if (t) nav(wiringHref({ dashboard_id: t.id, append: 1 })); }}
                onSchemaMismatch={(p) => { setTileSourceTarget(null); setSchemaMismatch(p); }}
            />
            <SchemaMatchErrorDialog
                open={!!schemaMismatch}
                onClose={() => setSchemaMismatch(null)}
                payload={schemaMismatch}
            />
        </div>
    );
}


/**
 * iter-121.131 — Minimal Prev / Page X of Y / Next footer used by
 * both "My dashboards" and "Prebuilts". Built inline rather than
 * lifted into shadcn `Pagination` because we only need the three
 * affordances + a small range label, and the shadcn primitive ships
 * styled `<a>` tags that we'd have to override to play nice with
 * SPA routing anyway.
 */
function PagerFooter({ page, pageCount, pageSize, total, onChange, testIdPrefix = "pager" }) {
    if (pageCount <= 1) return null;
    const start = (page - 1) * pageSize + 1;
    const end = Math.min(total, page * pageSize);
    return (
        <div
            className="mt-4 flex items-center justify-between gap-3 border-t border-border pt-3"
            data-testid={`${testIdPrefix}-pager`}
        >
            <div className="text-[11px] text-muted-foreground font-mono">
                <span data-testid={`${testIdPrefix}-pager-range`}>
                    {start}–{end} of {total}
                </span>
            </div>
            <div className="flex items-center gap-2">
                <Button
                    variant="outline" size="sm"
                    className="h-7 text-xs rounded-sm"
                    onClick={() => onChange(Math.max(1, page - 1))}
                    disabled={page <= 1}
                    data-testid={`${testIdPrefix}-pager-prev`}
                >
                    Prev
                </Button>
                <span
                    className="text-[11px] text-muted-foreground font-mono px-1"
                    data-testid={`${testIdPrefix}-pager-page`}
                >
                    Page {page} of {pageCount}
                </span>
                <Button
                    variant="outline" size="sm"
                    className="h-7 text-xs rounded-sm"
                    onClick={() => onChange(Math.min(pageCount, page + 1))}
                    disabled={page >= pageCount}
                    data-testid={`${testIdPrefix}-pager-next`}
                >
                    Next
                </Button>
            </div>
        </div>
    );
}
